import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Clock, Send, CheckCircle, Copy, Check, Camera, Smartphone, Download, Image, ArrowRight } from 'lucide-react'
import clsx from 'clsx'
import { apiUrl } from '../config/api'
import { PLATFORM_CONFIG } from '../data/mock'
import Button from '../components/ui/Button'

interface Mission {
  id: number; title: string; description: string | null
  action_type: string; target_platforms: string | null; platform_links: string | null; points: number
  points_per_platform: number; bonus_points: number; deadline: string | null
  status: string; category_name: string | null; category_color: string | null
}

interface MissionMedia {
  id: number; title: string; file_url: string; type: string; mime_type: string | null
}

const digitalTypes = ['post_social', 'share_whatsapp']
const linkTypes = ['repost', 'comment', 'engage']

const ACTION_LABELS: Record<string, { verb: string; instruction: string; button: string }> = {
  repost: { verb: 'Repostar', instruction: 'Abra o post e reposte/compartilhe nos seus stories ou feed', button: 'Abrir e repostar' },
  comment: { verb: 'Comentar', instruction: 'Abra o post e deixe seu comentário', button: 'Abrir e comentar' },
  engage: { verb: 'Engajar', instruction: 'Abra o post, reposte e comente', button: 'Abrir post' },
}

const PLATFORM_ICONS: Record<string, { name: string; color: string; deepLink?: (url: string) => string }> = {
  instagram: { name: 'Instagram', color: '#E4405F' },
  tiktok: { name: 'TikTok', color: '#010101' },
  youtube: { name: 'YouTube', color: '#FF0000' },
  facebook: { name: 'Facebook', color: '#1877F2' },
  twitter: { name: 'X (Twitter)', color: '#1DA1F2' },
  kwai: { name: 'Kwai', color: '#FF6F2C' },
}

export default function MissionDetailPage() {
  const { slug, missionId } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [mission, setMission] = useState<Mission | null>(null)
  const [textCopied, setTextCopied] = useState(false)
  const [media, setMedia] = useState<MissionMedia[]>([])
  const [links, setLinks] = useState<Record<string, string>>({})
  const [submitted, setSubmitted] = useState<Record<string, boolean>>({})
  const [submitting, setSubmitting] = useState<string | null>(null)

  useEffect(() => { fetchMission() }, [missionId])

  async function fetchMission() {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const headers = { Authorization: `Bearer ${token}`, Accept: 'application/json' }

      const res = await fetch(apiUrl(`/v1/community/missions?per_page=50`), { headers })
      const data = await res.json()
      const m = (data.data || []).find((x: any) => x.id === Number(missionId))
      if (m) setMission(m)

      const mediaRes = await fetch(apiUrl(`/v1/community/missions/${missionId}/media`), { headers })
      if (mediaRes.ok) {
        const mediaData = await mediaRes.json()
        setMedia(mediaData.data || [])
      }

      // Buscar submissions já feitas
      const subsRes = await fetch(apiUrl(`/v1/community/missions/${missionId}/my-submissions`), { headers })
      if (subsRes.ok) {
        const subsData = await subsRes.json()
        const done: Record<string, boolean> = {}
        for (const s of subsData.data || []) {
          if (s.platform) done[s.platform] = true
          else done['foto'] = true
        }
        setSubmitted(done)
      }
    } catch {} finally { setLoading(false) }
  }

  async function handleCopyText() {
    if (mission?.description) {
      try {
        await navigator.clipboard.writeText(mission.description)
      } catch {
        const ta = document.createElement('textarea')
        ta.value = mission.description
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
      }
      setTextCopied(true)
      setTimeout(() => setTextCopied(false), 2000)
    }
  }

  const [downloadingId, setDownloadingId] = useState<number | null>(null)
  const isFlutter = typeof (window as any).SispoliBridge !== 'undefined'
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

  function sendToFlutter(type: string, data: Record<string, unknown>) {
    try { (window as any).SispoliBridge?.postMessage(JSON.stringify({ type, ...data })) } catch {}
  }

  async function handleDownloadMedia(m: MissionMedia) {
    setDownloadingId(m.id)

    // Flutter WebView → salva via bridge nativo. fileName precisa da
    // extensão real pro app detectar imagem e salvar na Galeria/Fotos
    // (sem extensão, cai no fallback share sheet).
    if (isFlutter) {
      const ext = m.file_url.split('?')[0].split('.').pop() || ''
      const baseName = m.title.replace(/\.[a-z0-9]+$/i, '')
      const fileName = ext ? `${baseName}.${ext}` : m.title
      sendToFlutter('download_file', { url: m.file_url, fileName, mimeType: m.mime_type })
      setTimeout(() => setDownloadingId(null), 1000)
      return
    }

    // Mobile → share API pra "Salvar imagem"
    if (isMobile) {
      try {
        const res = await fetch(m.file_url)
        const blob = await res.blob()
        const file = new File([blob], m.title, { type: blob.type })

        if (navigator.share && navigator.canShare?.({ files: [file] })) {
          await navigator.share({ files: [file], title: m.title })
          setDownloadingId(null)
          return
        }

        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = m.title
        a.style.display = 'none'
        document.body.appendChild(a)
        a.click()
        setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url) }, 100)
      } catch {
        window.open(m.file_url, '_blank')
      } finally {
        setDownloadingId(null)
      }
      return
    }

    // Desktop → download via blob
    try {
      const res = await fetch(m.file_url)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = m.title
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      window.open(m.file_url, '_blank')
    } finally {
      setDownloadingId(null)
    }
  }

  function handleLinkChange(platform: string, value: string) {
    setLinks(prev => ({ ...prev, [platform]: value }))
  }

  async function handleSubmitLink(platform: string) {
    if (!links[platform]?.trim()) return
    setSubmitting(platform)
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(apiUrl(`/v1/community/missions/${missionId}/submit`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ platform, post_url: links[platform] }),
      })
      if (res.ok) setSubmitted(prev => ({ ...prev, [platform]: true }))
    } catch {} finally { setSubmitting(null) }
  }

  const photoInputRef = useRef<HTMLInputElement>(null)

  async function handlePhotoSelected(file: File) {
    setSubmitting('foto')
    try {
      const token = localStorage.getItem('token')
      const fd = new FormData()
      fd.append('photo', file)
      const res = await fetch(apiUrl(`/v1/community/missions/${missionId}/submit`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      })
      if (res.ok) setSubmitted(prev => ({ ...prev, foto: true }))
    } catch {} finally { setSubmitting(null) }
  }

  if (loading) {
    return (
      <div className="flex-1 overflow-y-auto p-4 md:p-6 animate-pulse">
        <div className="max-w-4xl mx-auto space-y-4">
          <div className="h-8 w-48 bg-gray-200 rounded" />
          <div className="h-20 bg-gray-100 rounded-xl" />
          <div className="h-32 bg-gray-100 rounded-xl" />
        </div>
      </div>
    )
  }

  if (!mission) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-content-muted">Missão não encontrada</p>
      </div>
    )
  }

  const isDigital = digitalTypes.includes(mission.action_type)
  const isLinkType = linkTypes.includes(mission.action_type)
  const platforms: string[] = mission.target_platforms ? JSON.parse(mission.target_platforms) : []
  const platformLinks: Record<string, string> = mission.platform_links ? JSON.parse(mission.platform_links) : {}
  const actionLabel = ACTION_LABELS[mission.action_type]
  const deadlineDate = mission.deadline ? new Date(mission.deadline) : null
  const hoursLeft = deadlineDate ? Math.max(0, Math.floor((deadlineDate.getTime() - Date.now()) / (1000 * 60 * 60))) : null
  const submittedCount = Object.keys(submitted).filter(k => submitted[k]).length
  const allSubmitted = isDigital && platforms.length > 0 ? platforms.every(p => submitted[p]) : isLinkType ? submitted['foto'] : submitted['foto']

  // Número do passo atual
  let stepNum = 0

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="h-12 px-4 flex items-center gap-3 border-b border-border-subtle">
        <button onClick={() => navigate(`/${slug}/missoes`)} className="text-content-muted hover:text-content-normal">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-base font-semibold text-content-normal truncate">{mission.title}</h1>
      </div>

      <div className="p-4 md:p-6 lg:p-8 w-full space-y-5">

        {/* Recompensa destacada */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-2xl p-5 text-center">
          <p className="text-xs text-content-muted mb-1 uppercase tracking-wide font-semibold">Recompensa</p>
          <p className="text-3xl font-black text-primary">+{mission.points} pts</p>
          {mission.bonus_points > 0 && (
            <p className="text-sm text-content-muted mt-1">+ {mission.bonus_points} pts bônus se completar todas as redes</p>
          )}
          {hoursLeft !== null && (
            <div className="flex items-center justify-center gap-1 mt-3 text-xs text-content-muted">
              <Clock size={12} />
              <span>{hoursLeft > 0 ? `${hoursLeft}h restantes` : 'Encerrada'}</span>
            </div>
          )}
        </div>

        {/* Passo a passo */}
        <div>
          <h2 className="text-sm font-bold text-content-normal mb-4 uppercase tracking-wide">Como participar</h2>

          <div className="space-y-8">

            {/* PASSO: Material (foto + texto lado a lado no desktop) */}
            {(media.length > 0 || (mission.description && isDigital)) && (
              <StepCard step={++stepNum} title="Salve o material e copie a legenda">
                <p className="text-xs text-content-muted mb-3">Salve a imagem no celular e copie a legenda pra sua postagem.</p>

                <div className="flex flex-col lg:flex-row gap-4">
                  {/* Fotos */}
                  {media.length > 0 && (
                    <div className={clsx('grid grid-cols-2 gap-3', mission.description && isDigital ? 'lg:w-1/2' : 'w-full max-w-md')}>
                      {media.map(m => (
                        <div key={m.id} className="rounded-xl border border-border-subtle overflow-hidden">
                          {m.type === 'image' && (
                            <img src={m.file_url} alt={m.title} className="w-full aspect-square object-cover" />
                          )}
                          <div className="flex items-center justify-between p-2 bg-surface-secondary">
                            <span className="text-[10px] text-content-normal font-medium truncate flex-1 mr-1">{m.title}</span>
                            <Button
                              size="sm"
                              loading={downloadingId === m.id}
                              onClick={() => handleDownloadMedia(m)}
                              icon={<Download size={12} />}
                            >
                              Salvar
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Legenda */}
                  {mission.description && isDigital && (
                    <div className={clsx(media.length > 0 ? 'lg:w-1/2' : 'w-full')}>
                      <p className="text-xs font-semibold text-content-muted mb-2 uppercase tracking-wide">Legenda</p>
                      <div
                        className="bg-surface-secondary rounded-xl p-4 border border-border-subtle cursor-pointer hover:border-primary/30 transition-colors mb-3"
                        onClick={handleCopyText}
                      >
                        <p className="text-sm text-content-normal leading-relaxed whitespace-pre-wrap">{mission.description}</p>
                      </div>
                      <Button
                        fullWidth
                        variant={textCopied ? 'primary' : 'secondary'}
                        onClick={handleCopyText}
                        icon={textCopied ? <Check size={16} /> : <Copy size={16} />}
                      >
                        {textCopied ? 'Copiado!' : 'Copiar legenda'}
                      </Button>
                    </div>
                  )}
                </div>
              </StepCard>
            )}

            {/* PASSO: Postar e enviar link (digital) */}
            {isDigital && platforms.length > 0 && (
              <StepCard step={++stepNum} title="Publique e envie o link">
                <p className="text-xs text-content-muted mb-4">
                  Poste nas redes abaixo e cole o link aqui. Você ganha <span className="text-primary font-bold">+{mission.points_per_platform} pts</span> por cada rede.
                </p>

                {/* Progresso */}
                {platforms.length > 1 && (
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex-1 h-2.5 bg-surface-active rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all duration-500"
                        style={{ width: `${(submittedCount / platforms.length) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-content-muted font-bold">{submittedCount}/{platforms.length}</span>
                  </div>
                )}

                <div className="space-y-3">
                  {platforms.map(platform => {
                    const config = PLATFORM_CONFIG[platform]
                    if (!config) return null
                    const isSubmitted = submitted[platform]
                    const linkValue = links[platform] || ''

                    return (
                      <div key={platform} className={clsx(
                        'rounded-xl border p-3 sm:p-4 transition-all',
                        isSubmitted ? 'border-status-online/30 bg-status-online/5' : 'border-border-subtle',
                      )}>
                        <div className="flex items-center gap-2.5 mb-3">
                          <div
                            className="w-9 h-9 shrink-0 rounded-xl flex items-center justify-center text-white font-bold text-sm"
                            style={{ backgroundColor: config.color }}
                          >
                            {config.label.charAt(0)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-content-normal truncate">{config.label}</p>
                            <p className="text-[11px] text-content-muted">+{mission.points_per_platform} pts</p>
                          </div>
                          {isSubmitted && (
                            <div className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-full bg-status-online/10 text-status-online">
                              <CheckCircle size={14} />
                              <span className="text-xs font-bold">Enviado</span>
                            </div>
                          )}
                        </div>

                        {!isSubmitted && (
                          <div className="flex gap-2">
                            <input
                              type="url"
                              value={linkValue}
                              onChange={e => handleLinkChange(platform, e.target.value)}
                              placeholder={`Link do ${config.label}…`}
                              className="flex-1 min-w-0 px-3 py-2.5 bg-surface-secondary text-content-normal text-sm rounded-xl border border-border-subtle focus:outline-none focus:border-primary transition-colors placeholder:text-content-muted"
                            />
                            <button
                              onClick={() => handleSubmitLink(platform)}
                              disabled={!linkValue.trim() || submitting === platform}
                              className={clsx(
                                'shrink-0 w-11 h-11 flex items-center justify-center rounded-xl text-sm font-bold transition-all',
                                linkValue.trim() ? 'bg-primary text-white active:scale-95' : 'bg-surface-active text-content-muted cursor-not-allowed',
                              )}
                              aria-label="Enviar link"
                            >
                              {submitting === platform ? '…' : <Send size={16} />}
                            </button>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </StepCard>
            )}

            {/* Descrição/detalhes da missão presencial — aparece ANTES da comprovação */}
            {!isDigital && mission.description && (
              <StepCard step={++stepNum} title="Detalhes da missão">
                <p className="text-sm text-content-normal leading-relaxed whitespace-pre-wrap">{mission.description}</p>
              </StepCard>
            )}

            {/* PASSO: Repost / Comment / Engage — abrir links */}
            {isLinkType && actionLabel && (
              <StepCard step={++stepNum} title={actionLabel.verb}>
                <p className="text-xs text-content-muted mb-4">{actionLabel.instruction}</p>

                {mission.description && (
                  <div className="mb-4 p-3 rounded-xl bg-surface-secondary">
                    <p className="text-xs font-semibold text-content-muted mb-1">
                      {mission.action_type === 'comment' || mission.action_type === 'engage' ? 'Texto sugerido pra comentar:' : 'Instruções:'}
                    </p>
                    <p className="text-sm text-content-normal whitespace-pre-wrap">{mission.description}</p>
                    {(mission.action_type === 'comment' || mission.action_type === 'engage') && (
                      <button onClick={handleCopyText} className="mt-2 flex items-center gap-1.5 text-xs text-primary font-semibold">
                        {textCopied ? <><Check size={14} /> Copiado!</> : <><Copy size={14} /> Copiar texto</>}
                      </button>
                    )}
                  </div>
                )}

                <div className="space-y-3">
                  {Object.entries(platformLinks).map(([platform, url]) => {
                    const info = PLATFORM_ICONS[platform] || { name: platform, color: '#666' }
                    return (
                      <a
                        key={platform}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-4 rounded-xl border border-border-subtle hover:border-primary/30 bg-surface-secondary hover:bg-primary/5 transition-all active:scale-[0.98]"
                      >
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: info.color }}>
                          {info.name.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-content-normal">{actionLabel.button}</p>
                          <p className="text-xs text-content-muted truncate">{info.name}</p>
                        </div>
                        <ArrowRight size={16} className="text-content-muted" />
                      </a>
                    )
                  })}
                </div>
              </StepCard>
            )}

            {/* PASSO: Print de comprovação (repost/comment/engage) */}
            {isLinkType && (
              <StepCard step={++stepNum} title="Envie o print de comprovação">
                <p className="text-xs text-content-muted mb-4">
                  Tire um print mostrando que você {mission.action_type === 'comment' ? 'comentou' : mission.action_type === 'engage' ? 'repostou e comentou' : 'repostou'}. Você ganha <span className="text-primary font-bold">+{mission.points} pts</span>.
                </p>

                {submitted['foto'] ? (
                  <div className="flex items-center gap-3 text-status-online bg-status-online/10 rounded-xl p-4">
                    <CheckCircle size={24} />
                    <div>
                      <p className="text-sm font-bold">Print enviado!</p>
                      <p className="text-xs text-content-muted">Será analisado e os pontos creditados em breve.</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={e => { const f = e.target.files?.[0]; if (f) handlePhotoSelected(f) }}
                    />
                    <button
                      onClick={() => photoInputRef.current?.click()}
                      disabled={submitting === 'foto'}
                      className="w-full flex items-center justify-center gap-3 p-6 rounded-xl border-2 border-dashed border-primary/30 hover:border-primary bg-primary/5 hover:bg-primary/10 transition-all cursor-pointer active:scale-[0.98]"
                    >
                      {submitting === 'foto' ? (
                        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Image size={24} className="text-primary" />
                      )}
                      <div className="text-left">
                        <p className="text-sm font-bold text-content-normal">
                          {submitting === 'foto' ? 'Enviando...' : 'Enviar print'}
                        </p>
                        <p className="text-xs text-content-muted">Foto da tela mostrando a comprovação</p>
                      </div>
                    </button>
                  </>
                )}
              </StepCard>
            )}

            {/* PASSO: Foto (presencial) — sempre por último */}
            {!isDigital && !isLinkType && (
              <StepCard step={++stepNum} title="Envie a comprovação">
                <p className="text-xs text-content-muted mb-4">
                  Tire uma foto como prova e envie. Você ganha <span className="text-primary font-bold">+{mission.points} pts</span>.
                </p>

                {submitted['foto'] ? (
                  <div className="flex items-center gap-3 text-status-online bg-status-online/10 rounded-xl p-4">
                    <CheckCircle size={24} />
                    <div>
                      <p className="text-sm font-bold">Foto enviada!</p>
                      <p className="text-xs text-content-muted">Será analisada e os pontos creditados em breve.</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      capture={isMobile ? 'environment' : undefined}
                      className="hidden"
                      onChange={e => {
                        const f = e.target.files?.[0]
                        if (f) handlePhotoSelected(f)
                      }}
                    />
                    <button
                      onClick={() => photoInputRef.current?.click()}
                      disabled={submitting === 'foto'}
                      className="w-full flex items-center justify-center gap-3 p-6 rounded-xl border-2 border-dashed border-primary/30 hover:border-primary bg-primary/5 hover:bg-primary/10 transition-all cursor-pointer active:scale-[0.98]"
                    >
                      {submitting === 'foto' ? (
                        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Camera size={24} className="text-primary" />
                      )}
                      <div className="text-left">
                        <p className="text-sm font-bold text-content-normal">
                          {submitting === 'foto' ? 'Enviando foto...' : isMobile ? 'Tirar foto' : 'Enviar foto da galeria'}
                        </p>
                        <p className="text-xs text-content-muted">{isMobile ? 'Abre a câmera do celular' : 'JPG, PNG'}</p>
                      </div>
                    </button>
                  </>
                )}
              </StepCard>
            )}
          </div>
        </div>

        {/* Bônus */}
        {allSubmitted && mission.bonus_points > 0 && (
          <div className="p-5 rounded-2xl bg-gradient-to-r from-status-idle/10 to-status-idle/5 border border-status-idle/30 text-center">
            <p className="text-3xl mb-2">🎉</p>
            <p className="text-lg font-black text-status-idle">Bônus desbloqueado!</p>
            <p className="text-sm text-content-muted mt-1">Você postou em todas as redes!</p>
            <p className="text-2xl font-black text-status-idle mt-2">+{mission.bonus_points} pts</p>
          </div>
        )}
      </div>
    </div>
  )
}

/** Card de passo numerado */
function StepCard({ step, title, children }: { step: number; title: string; children: React.ReactNode }) {
  return (
    <div className="relative pl-10">
      {/* Número do passo */}
      <div className="absolute left-0 top-0 w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold">
        {step}
      </div>
      <div>
        <h3 className="text-sm font-bold text-content-normal mb-2">{title}</h3>
        {children}
      </div>
    </div>
  )
}
